/**
 * Catálogo estático de documentos legales/comerciales — Comercial 3A.
 * Fuente de verdad de textos: docs/legal/*.md
 * Persistencia de aceptación: Comercial 3B/3C (pendiente).
 */

export const LEGAL_COMMERCIAL_3_VERSION = "1.0";

export const LEGAL_SYSTEM_COMMERCIAL_NAME = "BNT Sistema Empresarial";

/** @deprecated Prefer LEGAL_INFO_BANNER — se mantiene por compatibilidad de tests legacy. */
export const LEGAL_DESIGN_BANNER =
  "Documentos legales versión 1.0 — vista informativa. Sujetos a revisión legal externa antes de enforcement.";

export const LEGAL_INFO_BANNER =
  "Documentos legales versión 1.0 — vista informativa. La aceptación electrónica persistida se habilitará en Comercial 3C.";

/** Versión global mostrada en hub (Comercial 3 base). */
export const LEGAL_DOCUMENT_VERSION = LEGAL_COMMERCIAL_3_VERSION;

export const LEGAL_PROVIDER_BNT = {
  legal_name: "BNT Visión y Logística Empresarial S.A.S. de C.V.",
  rfc: "BVL1908211D6",
  legal_representative: "CP José Baldemar Buentello Montes",
  address: "Emilio Carranza 828, colonia Monterrey Dentro, Monterrey, Nuevo León, México",
  privacy_contact_email: "despachobnt.mty@gmail.com",
};

/** IDs canónicos Comercial 3 — bundle solicitud de licencia. */
export const COMMERCIAL_3_LEGAL_DOCUMENT_IDS = [
  "terms_of_use",
  "privacy_notice",
  "module_scope",
  "responsibilities_support_warranty",
];

/** Bundle legal requerido al enviar solicitud de licencia (enforcement en 3C). */
export const LICENSE_REQUEST_LEGAL_BUNDLE = [...COMMERCIAL_3_LEGAL_DOCUMENT_IDS];

/** Módulos comerciales documentados (sin control_despacho). */
export const LEGAL_MODULE_CATALOG = [
  { id: "visor", label: "Visor Fiscal BNT" },
  { id: "laboral", label: "Laboral / REPSE" },
  { id: "tramites", label: "Trámites Fiscales" },
  { id: "despacho", label: "Gestión Despacho" },
];

/**
 * Alias legacy → ID canónico Comercial 3.
 * @type {Record<string, string>}
 */
export const LEGAL_DOCUMENT_ID_ALIASES = {
  "aviso-privacidad": "privacy_notice",
  "terminos-uso": "terms_of_use",
  "carta-uso": "terms_of_use",
  "alcance-responsabilidades": "responsibilities_support_warranty",
};

/**
 * @typedef {object} LegalDocumentMeta
 * @property {string} id
 * @property {string} title
 * @property {string} summary
 * @property {string[]} requiredAt
 * @property {string} [version]
 * @property {string} [sourcePath]
 * @property {string} [hash]
 * @property {boolean} [commercial3]
 * @property {string[]} [legacyIds]
 */

/** @type {LegalDocumentMeta[]} */
export const COMMERCIAL_3_LEGAL_DOCUMENTS = [
  {
    id: "terms_of_use",
    legacyIds: ["terminos-uso", "carta-uso"],
    title: "Términos y condiciones de uso de BNT Sistema Empresarial",
    summary:
      "Condiciones generales de acceso y uso de BNT Sistema Empresarial; licencia limitada, usuarios, propiedad intelectual y vigencia.",
    version: LEGAL_COMMERCIAL_3_VERSION,
    sourcePath: "docs/legal/TERMINOS_USO_BNT_SISTEMA_EMPRESARIAL_v1.md",
    hash: "297a665bd2f037d1c04ff566f3e6229e0a100dcb7f2026719a37b74f16ef98c3",
    requiredAt: ["license_request", "registro", "activacion"],
    commercial3: true,
  },
  {
    id: "privacy_notice",
    legacyIds: ["aviso-privacidad"],
    title: "Aviso de privacidad",
    summary:
      "Tratamiento de datos personales, fiscales, contables, laborales y de terceros; finalidades, seguridad y contacto de privacidad.",
    version: LEGAL_COMMERCIAL_3_VERSION,
    sourcePath: "docs/legal/AVISO_PRIVACIDAD_BNT_v1.md",
    hash: "b2cca23e2cdbd10cf1744667b1bf35dfbbb48ea610caa8a62959d956b37e21e2",
    requiredAt: ["license_request", "registro", "activacion"],
    commercial3: true,
  },
  {
    id: "module_scope",
    legacyIds: [],
    title: "Anexo A — Alcance de módulos contratados",
    summary:
      "Descripción de Visor Fiscal BNT, Laboral / REPSE, Trámites Fiscales y Gestión Despacho; acceso según licencia.",
    version: LEGAL_COMMERCIAL_3_VERSION,
    sourcePath: "docs/legal/ALCANCE_MODULOS_BNT_v1.md",
    hash: "7cf79ffff97ba200b624307f9d619bf58d68b799961cb9f4d490e095800802be",
    requiredAt: ["license_request", "registro", "activacion"],
    commercial3: true,
  },
  {
    id: "responsibilities_support_warranty",
    legacyIds: ["alcance-responsabilidades"],
    title: "Anexo B/C — Responsabilidades, soporte, garantía limitada y reembolso proporcional",
    summary:
      "Responsabilidades de BNT y del cliente, soporte, garantía limitada, exclusiones y tope de responsabilidad.",
    version: LEGAL_COMMERCIAL_3_VERSION,
    sourcePath: "docs/legal/RESPONSABILIDADES_SOPORTE_GARANTIA_BNT_v1.md",
    hash: "127d38f6968da70811a3b98abb0c96e6dcca485e8579fed42a73f031a902a595",
    requiredAt: ["license_request", "registro", "activacion"],
    commercial3: true,
  },
];

/** Documentos complementarios (fases posteriores al bundle Comercial 3). */
/** @type {LegalDocumentMeta[]} */
export const LEGACY_SUPPLEMENTAL_LEGAL_DOCUMENTS = [
  {
    id: "autorizaciones-comprador",
    title: "Autorizaciones del comprador sobre sus clientes",
    summary:
      "El despacho declara autorización de sus clientes para registrar y administrar información sensible.",
    requiredAt: ["registro", "alta_cliente"],
  },
  {
    id: "politica-baja",
    title: "Política de baja del cliente",
    summary: "Revocación, baja y atención de solicitudes del cliente final.",
    requiredAt: ["registro"],
  },
  {
    id: "politica-cambio-despacho",
    title: "Política de cambio de despacho",
    summary: "Transferencia entre despachos solo con autorización expresa del cliente.",
    requiredAt: ["registro"],
  },
  {
    id: "tratamiento-fiscal",
    title: "Autorización para tratamiento y resguardo de información fiscal",
    summary: "Alcance del resguardo de XML, CFDI y credenciales referenciadas en el sistema.",
    requiredAt: ["activacion", "primer_uso_sat"],
  },
  {
    id: "politica-licencia-temporal",
    title: "Política de licencia temporal / demo autorizada",
    summary:
      "Extensiones de evaluación por acto comercial; no reinicio automático del trial.",
    requiredAt: ["licencia_temporal"],
  },
];

/** Catálogo completo: Comercial 3 + complementarios. */
export const LEGAL_DOCUMENTS_CATALOG = [
  ...COMMERCIAL_3_LEGAL_DOCUMENTS,
  ...LEGACY_SUPPLEMENTAL_LEGAL_DOCUMENTS,
];

/** Texto futuro — aceptación en solicitud de licencia (Comercial 3C). */
export const LICENSE_REQUEST_ACCEPTANCE_LABEL =
  "He leído y acepto los Términos y condiciones, el Aviso de privacidad, el Alcance de módulos y las Responsabilidades y garantía limitada de BNT Sistema Empresarial.";

export const REGISTRATION_ACCEPTANCE_LABEL =
  "Acepto el aviso de privacidad, términos de uso, carta de uso del sistema, autorizaciones y alcance de responsabilidades.";

/** @param {string} docId */
export function resolveLegalDocumentId(docId) {
  const key = String(docId || "").trim();
  return LEGAL_DOCUMENT_ID_ALIASES[key] || key;
}

/** @param {string} docId */
export function getLegalDocumentById(docId) {
  const canonical = resolveLegalDocumentId(docId);
  return LEGAL_DOCUMENTS_CATALOG.find((d) => d.id === canonical) || null;
}

/** @param {string} origin */
export function listDocumentsRequiredAt(origin) {
  return LEGAL_DOCUMENTS_CATALOG.filter((d) => d.requiredAt.includes(origin));
}

/** Documentos del bundle license_request (Comercial 3). */
export function listLicenseRequestLegalBundle() {
  return LICENSE_REQUEST_LEGAL_BUNDLE.map((id) => getLegalDocumentById(id)).filter(Boolean);
}

/** @param {string} docId */
export function isCommercial3LegalDocument(docId) {
  const doc = getLegalDocumentById(docId);
  return Boolean(doc?.commercial3);
}
